/**
 * TestSuitesPanel Component
 * Test suite management panel for the Evaluation Center
 * Extracted from TestSuitesPage for tab-based layout
 */

import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { RefreshCw, FolderPlus, History } from 'lucide-react';
import { useI18n } from '@/i18n';
import {
  CreateTestSuiteDialog,
  EvalRunsPanel,
  useTestSuites,
  useUpdateTestSuite,
  useDeleteTestSuite,
  useRunTestSuite,
} from '@/features/eval-scenarios';
import { TestSuiteList } from '@/features/eval-scenarios/components/TestSuiteList';
import type { EvalTestSuite, UpdateTestSuiteRequest, CreateTestSuiteRequest } from '@/types/evalScenarios';

export function TestSuitesPanel() {
  const { messages } = useI18n();
  const t = messages.evalScenarios.testSuites;

  // State
  const [editSuite, setEditSuite] = useState<EvalTestSuite | null>(null);
  const [showRunsPanel, setShowRunsPanel] = useState(false);
  const [runningIds, setRunningIds] = useState<Set<string>>(new Set());

  // Queries
  const { data: suites = [], isLoading, refetch } = useTestSuites();

  // Mutations
  const updateMutation = useUpdateTestSuite();
  const deleteMutation = useDeleteTestSuite();
  const runMutation = useRunTestSuite();

  // Handlers
  const handleEdit = (suite: EvalTestSuite) => {
    setEditSuite(suite);
  };

  const handleUpdate = async (data: CreateTestSuiteRequest | UpdateTestSuiteRequest) => {
    if (!editSuite) return;
    try {
      await updateMutation.mutateAsync({
        suiteId: editSuite.id,
        data: data as UpdateTestSuiteRequest,
      });
      setEditSuite(null);
    } catch (error) {
      console.error('Failed to update suite:', error);
    }
  };

  const handleDelete = async (suite: EvalTestSuite) => {
    const confirmMsg = (t?.panel?.deleteConfirm || 'Are you sure you want to delete "{name}"?')
      .replace('{name}', suite.name);
    if (!confirm(confirmMsg)) return;

    try {
      await deleteMutation.mutateAsync({ suiteId: suite.id });
    } catch (error) {
      console.error('Failed to delete suite:', error);
    }
  };

  const handleRun = async (suite: EvalTestSuite) => {
    setRunningIds((prev) => new Set(prev).add(suite.id));
    try {
      await runMutation.mutateAsync({ suiteId: suite.id });
      setShowRunsPanel(true);
    } catch (error) {
      console.error('Failed to run suite:', error);
    } finally {
      setRunningIds((prev) => {
        const next = new Set(prev);
        next.delete(suite.id);
        return next;
      });
    }
  };

  const handleViewScenarios = (suite: EvalTestSuite) => {
    const msg = (t?.panel?.containsScenarios || 'Suite "{name}" contains {count} scenarios')
      .replace('{name}', suite.name)
      .replace('{count}', String(suite.scenario_count));
    alert(msg);
  };

  // Stats
  const activeSuites = suites.filter((s) => s.is_active);
  const totalScenarios = suites.reduce((sum, s) => sum + s.scenario_count, 0);

  return (
    <>
      {/* Actions Bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => setShowRunsPanel(true)}
          >
            <History className="w-4 h-4 mr-2" />
            {t?.panel?.runHistory || 'Run History'}
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {t?.panel?.refresh || 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500 mb-1">
            {t?.panel?.totalSuites || 'Total Suites'}
          </p>
          <p className="text-2xl font-bold text-gray-900">{suites.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500 mb-1">
            {t?.active || 'Active'}
          </p>
          <p className="text-2xl font-bold text-violet-600">{activeSuites.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500 mb-1">
            {t?.panel?.totalScenarios || 'Total Scenarios'}
          </p>
          <p className="text-2xl font-bold text-indigo-600">{totalScenarios}</p>
        </div>
      </div>

      {/* Tip */}
      <div className="mb-6 bg-violet-50 border border-violet-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FolderPlus className="w-5 h-5 text-violet-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-violet-900">
              {t?.panel?.howToCreate || 'How to create a Suite?'}
            </p>
            <p className="text-sm text-violet-700 mt-1">
              {t?.panel?.howToCreateHint || 'Go to the "Scenarios" tab, click "Select for Suite" button, select scenarios and create.'}
            </p>
          </div>
        </div>
      </div>

      {/* Suite List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 bg-white rounded-lg border border-gray-200">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600 mx-auto mb-2" />
            <p className="text-sm text-gray-500">
              {t?.panel?.loading}
            </p>
          </div>
        </div>
      ) : (
        <TestSuiteList
          suites={suites}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRun={handleRun}
          onViewScenarios={handleViewScenarios}
          runningIds={runningIds}
        />
      )}

      {/* Edit Dialog */}
      <CreateTestSuiteDialog
        isOpen={!!editSuite}
        onClose={() => setEditSuite(null)}
        onSubmit={handleUpdate}
        suite={editSuite}
        isSubmitting={updateMutation.isPending}
      />

      {/* Runs Panel */}
      <EvalRunsPanel
        isOpen={showRunsPanel}
        onClose={() => setShowRunsPanel(false)}
      />
    </>
  );
}
